/**
 * LockedFeature — full-page lock card shown to pay-per-conversion (role-less)
 * users when they open a member-only page. The backend enforces the same lock
 * server-side; this is the friendly explanation + upgrade path.
 */
import { Link } from 'react-router-dom';

import { Card } from './ui/card';
import { Button } from './ui/button';
import { Pill } from './ui/pill';

const PRICING_URL = 'https://anterotrail.com/#pricing';
const CONTACT_EMAIL = 'info@anterotrail.com';

/** Props for {@link LockedFeature}. */
export interface LockedFeatureProps {
  /** Human name of the locked feature (e.g. "Scope Builder"). */
  feature: string;
  /** One-line description of what the feature does. */
  description?: string;
}

/** Full-page locked-feature card with contact/pricing actions. */
export default function LockedFeature({ feature, description }: LockedFeatureProps) {
  return (
    <div className="grid h-full place-items-center px-6 py-10" data-testid="locked-feature">
      <Card className="w-full max-w-[460px] p-6 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[var(--bg-3)] border border-[var(--bd-subtle)] text-[20px]">
          🔒
        </div>
        <div className="mb-1 flex items-center justify-center gap-2">
          <h2 className="text-[16px] font-[600] text-[var(--tx-hi)]">{feature}</h2>
          <Pill kind="neutral">LOCKED</Pill>
        </div>
        <p className="text-[12.5px] text-[var(--tx-mid)]">
          {description ||
            `${feature} is part of the full Antero Trail plan.`}{' '}
          Your account has pay-per-conversion access: import estimate PDFs, review the
          full folder tree and line items, and export ESX files.
        </p>
        <p className="mt-2 text-[12px] text-[var(--tx-lo)]">
          Want the full toolkit? Email{' '}
          <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>{' '}
          and we&rsquo;ll set you up.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="secondary" size="sm" asChild>
            <a href={PRICING_URL} target="_blank" rel="noreferrer">
              See pricing
            </a>
          </Button>
          <Button variant="primary" size="sm" asChild>
            <Link to="/completeness">Go to Supplement</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}
